import { toAvatarSelection, sameAvatarIdentity } from "./avatar-selection-identity";

const assetIdFromValue = value => {
  if (!value || typeof value !== "string") return null;
  const match = value.match(/\/(?:assets|files)\/([^/.]+)(?:\.glb)?(?:[?#].*)?$/i);
  return match?.[1] || null;
};

const identityTokens = value => {
  const normalized = typeof value === "string" ? value.trim() : value == null ? "" : String(value);
  if (!normalized) return new Set();
  const assetId = assetIdFromValue(normalized);
  return new Set([normalized, ...(assetId ? [assetId] : [])]);
};

export const avatarIdentityMatches = (current, deleted) => {
  const currentTokens = identityTokens(current);
  const deletedTokens = new Set(
    [deleted?.id, deleted?.sid, deleted?.assetId, deleted?.gltfUrl].flatMap(value => [...identityTokens(value)])
  );
  return [...currentTokens].some(token => deletedTokens.has(token));
};

export const replacementToMediaEntry = replacement => {
  if (!replacement) return null;
  const sid = replacement.sid || (replacement.id == null ? null : String(replacement.id));
  const id = sid || (replacement.id == null ? null : String(replacement.id));
  return {
    id,
    avatar_id: id,
    uuid: replacement.id == null ? null : String(replacement.id),
    sid,
    asset_id: replacement.assetId || null,
    gltfs: { avatar: replacement.gltfUrl || "" },
    images: { preview: { url: replacement.thumbnailUrl || null } }
  };
};

export async function deletePersistenceAvatar({ avatar, fetchImpl = fetch, authHeaders, persistenceApiUrl }) {
  const avatarId = avatar?.id || avatar?.avatar_id;
  if (!avatarId || !avatar?.workspace_id) {
    throw new Error("Cannot delete avatar without its Workspace identity.");
  }
  if (!persistenceApiUrl || !authHeaders) {
    throw new Error("Avatar deletion API dependencies are unavailable.");
  }
  const url = `${persistenceApiUrl}/workspaces/${avatar.workspace_id}/avatars/${avatarId}`;
  const response = await fetchImpl(url, {
    method: "DELETE",
    headers: await authHeaders(url, "DELETE")
  });
  if (!response.ok) {
    throw new Error(`Persistence API delete failed: ${response.status}`);
  }
  return response.json();
}

const avatarRecordToReplacement = (value, source) => {
  const record = value?.data || value;
  if (!record || typeof record !== "object") return null;
  return {
    source,
    id: record.id ?? record.avatar_id ?? null,
    sid: record.sid ?? record.avatar_sid ?? null,
    assetId: record.assetId ?? record.asset_id ?? null,
    gltfUrl: record.gltfUrl ?? record.gltf_url ?? record.url ?? record.gltfs?.avatar ?? "",
    thumbnailUrl: record.thumbnailUrl ?? record.thumbnail_url ?? record.thumbnail ?? record.images?.preview?.url ?? null
  };
};

export async function resolveAvatarFallback({ workspaceId, fetchImpl = fetch, authHeaders, persistenceApiUrl }) {
  if (!persistenceApiUrl || !authHeaders) {
    throw new Error("Avatar fallback API dependencies are unavailable.");
  }
  if (workspaceId) {
    const query = new URLSearchParams({
      page: "1",
      per_page: "1",
      orderBy: "created_at",
      orderDirection: "desc"
    });
    const url = `${persistenceApiUrl}/workspaces/${workspaceId}/avatars?${query}`;
    try {
      const response = await fetchImpl(url, {
        method: "GET",
        headers: await authHeaders(url, "GET")
      });
      if (response.ok) {
        const payload = await response.json();
        const newest = Array.isArray(payload?.data) ? payload.data[0] : null;
        if (newest) return replacementToMediaEntry(avatarRecordToReplacement(newest, "personal"));
      }
    } catch {
      // A personal-catalog outage still permits a public platform fallback.
    }
  }

  const platformUrl = `${persistenceApiUrl}/avatars/default`;
  const response = await fetchImpl(platformUrl, {
    method: "GET",
    headers: { Accept: "application/json" }
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Persistence API default avatar failed: ${response.status}`);
  }
  const payload = await response.json();
  return replacementToMediaEntry(avatarRecordToReplacement(payload, "platform"));
}

const avatarEntryUrl = avatar => avatar?.gltf_url || avatar?.url || avatar?.gltfs?.avatar || null;

const avatarEntryId = avatar => avatar?.avatar_id || avatar?.sid || avatar?.id || null;

const fetchedAvatarMatches = (avatarId, avatar) => {
  if (!avatar) return false;
  if (String(avatarEntryId(avatar) || "") === String(avatarId)) return true;
  return avatarIdentityMatches(avatarId, {
    id: avatar?.id,
    sid: avatar?.sid || avatar?.avatar_id,
    assetId: avatar?.asset_id,
    gltfUrl: avatarEntryUrl(avatar)
  });
};

export async function reconcileAvatarSelection({
  avatarId,
  currentAvatarUrl,
  workspaceId,
  store,
  dashboardChannel,
  fetchAvatarImpl,
  resolveFallbackImpl,
  isCurrent = () => true,
  persist = true,
  persistAvatar = value => dashboardChannel.updateCurrentUserProfile(value)
}) {
  const accountId = store.state.authUser?.id;
  const current = () => isCurrent() && store.state.authUser?.id === accountId;
  const staleResult = () => ({ persisted: false, stale: true, avatarId });
  const updateStore = patch =>
    store.update(patch, {
      arrayMerge: (_destinationArray, sourceArray) => sourceArray
    });
  if (!current()) return staleResult();

  let entry = null;
  let fallback = false;
  if (avatarId != null) {
    try {
      entry = await fetchAvatarImpl(avatarId);
    } catch {
      entry = null;
    }
    if (!current()) return staleResult();
    if (!fetchedAvatarMatches(avatarId, entry)) {
      entry = await resolveFallbackImpl({ workspaceId });
      if (!current()) return staleResult();
      fallback = true;
    }
  }
  const selection = entry ? toAvatarSelection(fallback ? avatarEntryId(entry) : avatarId, entry) : null;
  const nextId = selection?.avatarId ?? null;
  const nextUrl = selection?.avatarUrl ?? null;
  const thumbnail = selection?.thumbnail ?? null;
  if (!current()) return staleResult();
  if (store.state.profile?.avatarId !== nextId || store.state.profile?.thumbnail !== thumbnail) {
    updateStore({ profile: { ...store.state.profile, avatarId: nextId, thumbnail } });
  }
  const result = { persisted: false, avatarId: nextId, avatarUrl: nextUrl, selection, fallback };
  if (!current()) return staleResult();
  if (!persist) return result;
  if (sameAvatarIdentity(toAvatarSelection(currentAvatarUrl, null), selection)) {
    return { ...result, persisted: true };
  }
  if (!accountId || !dashboardChannel) return result;
  try {
    if (!current()) return staleResult();
    await persistAvatar({ avatar: nextUrl });
    if (!current()) return staleResult();
    updateStore({ authUser: { ...store.state.authUser, avatar: nextUrl } });
    return { ...result, persisted: true };
  } catch (error) {
    if (!current()) return staleResult();
    console.error("AvatarPersistenceManager: Failed to persist selected avatar", error);
    return { ...result, error };
  }
}
